import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, FileBadge, AlertTriangle, Trash2, FileText } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useTarifs, useCreateTarif, useUpdateTarif, useDeleteTarif,
  useBetonProduits,
} from '@/hooks/useBetonCatalog';
import { useContacts } from '@/hooks/useCrm';
import { STATUT_TARIF_LABELS, type StatutTarif, type TarifContratCadre } from '@/types';
import { formatMontant } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { generateDevisPDF } from '@/utils/beton-pdf';
import { demander } from '@/components/ui/Fenetres';

/**
 * Page de gestion des tarifs négociés en contrats cadre (Sprint LB-D).
 * Cible : DAF et manager pour saisir/maintenir les prix négociés avec
 * les gros clients récurrents (CIE, AGEROUTE, Orange, MTN, BTP grands comptes).
 */

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUT_BADGE: Record<StatutTarif, string> = {
  actif:   'ds-bd ds-bd-s',
  expire:  'ds-bd ds-bd-n',
  resilie: 'ds-bd ds-bd-d',
};

export const TarifsContratsPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutTarif | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<TarifContratCadre | undefined>();

  const { data, isLoading, refetch } = useTarifs({ statut: statut || undefined, limit: 100 });
  const tarifs   = data?.data ?? [];
  const remove   = useDeleteTarif();
  const [error, setError] = useState<string | null>(null);

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (t: TarifContratCadre) => { setEditing(t); setDialogOpen(true); };

  const onRemove = async (id: string, ref: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer le tarif « ${ref} » ?`, message: 'La suppression est définitive.', confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const onDevisPDF = async (t: TarifContratCadre) => {
    const qtyStr = await demander.texte({ titre: `Quantité à devis — ${t.produit?.code ?? ''}`, message: t.produit?.designation, libelle: 'Quantité', defaut: '1', min: 1, confirmer: 'Générer le devis' });
    if (qtyStr === null) return;
    const quantite = parseFloat(qtyStr.replace(',', '.'));
    if (!(quantite > 0)) { setError('Quantité invalide : saisissez un nombre supérieur à 0.'); return; }
    try {
      // Récup les détails complets du produit (norme, classe, fc28, longueur, effort)
      const produitFull = await import('@/api/operations').then((m) => m.operationsApi.beton.getProduit(t.produitId));
      const clientFull  = t.client;
      if (!clientFull || !filiale) { setError('Devis impossible : le client du tarif ou la société n’est pas chargé.'); return; }
      const numeroDevis = `DEV-${t.contratRef}-${new Date().toISOString().slice(0,10).replace(/-/g, '')}`;
      await generateDevisPDF({
        numeroDevis,
        dateEmission:  new Date().toISOString().slice(0, 10),
        validiteJours: 30,
        filiale: { name: filiale.name, fullName: filiale.fullName, pays: filiale.pays ?? 'CI', devise: filiale.devise ?? 'FCFA' },
        client:  { fullName: clientFull.fullName, companyName: clientFull.companyName ?? null },
        tvaPct:  18,
        lignes:  [{
          code:               produitFull.code,
          designation:        produitFull.designation,
          famille:            produitFull.famille,
          norme:              produitFull.norme ?? null,
          classeProduit:      produitFull.classeProduit ?? null,
          resistanceFc28:     produitFull.resistanceFc28 ?? null,
          longueurM:          produitFull.longueurM ? Number(produitFull.longueurM) : null,
          effortNominalDaN:   produitFull.effortNominalDaN ?? null,
          quantite,
          uniteVente:          produitFull.uniteVente,
          prixHTUnitaireCents: Number(BigInt(t.prixHTCents)),
          remisePct:           t.remisePct ? Number(t.remisePct) : undefined,
          contratRef:          t.contratRef,
        }],
        notesFooter: 'Conditions de paiement : 30 jours fin de mois. Pénalités de retard 1.5%/mois. Solidaire sur factures impayées au-delà de 60 jours.',
      });
    } catch (err) { setError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Tarifs contrats cadre"
        subtitle={filiale ? `Prix négociés grands clients · ${filiale.fullName}` : 'Tarifs'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}>
              <Plus size={14} /> Nouveau tarif
            </button>
          </>
        }
      />

      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-filters">
          <select
            className="ds-fi"
            value={statut}
            onChange={(e) => setStatut(e.target.value as StatutTarif | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_TARIF_LABELS) as StatutTarif[]).map((s) => (
              <option key={s} value={s}>{STATUT_TARIF_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Tarifs négociés</div>
              <div className="ds-table-sub">{tarifs.length} tarif{tarifs.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 140 }}>Contrat</th>
                <th>Client</th>
                <th>Produit</th>
                <th className="ds-am" style={{ width: 130 }}>Prix HT négocié</th>
                <th className="ds-am" style={{ width: 80 }}>Remise</th>
                <th style={{ width: 100 }}>Début</th>
                <th style={{ width: 100 }}>Fin</th>
                <th style={{ width: 80 }}>Statut</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && tarifs.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <FileBadge size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun tarif contrat cadre</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}>
                      <Plus size={12} /> Créer le premier tarif
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && tarifs.map((t) => (
                <tr key={t.id}>
                  <td><span className="ds-ref-code">{t.contratRef}</span></td>
                  <td style={{ fontSize: 12 }}>{t.client?.companyName ?? t.client?.fullName ?? '—'}</td>
                  <td>
                    <div className="ds-entry-label" style={{ fontSize: 12 }}>{t.produit?.designation ?? '—'}</div>
                    <div className="ds-entry-sub">{t.produit?.code}</div>
                  </td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{formatMontant(t.prixHTCents)} FCFA</td>
                  <td className="ds-am">{t.remisePct ? `-${t.remisePct}%` : '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(t.dateDebut)}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-3)' }}>{fmtDate(t.dateFin)}</td>
                  <td><span className={STATUT_BADGE[t.statut]}>{STATUT_TARIF_LABELS[t.statut]}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => void onDevisPDF(t)} className="ds-row-btn" title="Générer devis PDF (avec certificat NF si poteau BA)" style={{ color: 'var(--p)' }}>
                        <FileText size={13} />
                      </button>
                      <button onClick={() => openEdit(t)} className="ds-row-btn" title="Modifier">
                        <Edit size={13} />
                      </button>
                      <button onClick={() => onRemove(t.id, t.contratRef)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TarifFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} tarif={editing} />
    </>
  );
};

// ── Dialog création/édition tarif ────────────────────────────

const TarifFormDialog = ({ open, onClose, tarif }: { open: boolean; onClose: () => void; tarif?: TarifContratCadre }) => {
  const isEdit = !!tarif;
  const [clientId, setClientId]     = useState('');
  const [produitId, setProduitId]   = useState('');
  const [contratRef, setContratRef] = useState('');
  const [prixHT, setPrixHT]         = useState('');
  const [remisePct, setRemisePct]   = useState('');
  const [dateDebut, setDateDebut]   = useState('');
  const [dateFin, setDateFin]       = useState('');
  const [statut, setStatut]         = useState<StatutTarif>('actif');
  const [notes, setNotes]           = useState('');
  const [error, setError]           = useState<string | null>(null);

  const { data: contactsData } = useContacts({ contactType: 'client', limit: 500 });
  const { data: produitsData } = useBetonProduits({ actif: true, limit: 200 });
  const create = useCreateTarif();
  const update = useUpdateTarif();

  const clients  = contactsData?.data ?? [];
  const produits = produitsData?.data ?? [];

  useEffect(() => {
    if (!open) return;
    if (tarif) {
      setClientId(tarif.clientId);
      setProduitId(tarif.produitId);
      setContratRef(tarif.contratRef);
      setPrixHT(String(Number(BigInt(tarif.prixHTCents)) / 100));
      setRemisePct(tarif.remisePct ?? '');
      setDateDebut(tarif.dateDebut.slice(0, 10));
      setDateFin(tarif.dateFin?.slice(0, 10) ?? '');
      setStatut(tarif.statut);
      setNotes(tarif.notes ?? '');
    } else {
      setClientId(''); setProduitId(''); setContratRef('');
      setPrixHT(''); setRemisePct(''); setDateFin(''); setStatut('actif'); setNotes('');
      setDateDebut(new Date().toISOString().slice(0, 10));
    }
    setError(null);
  }, [open, tarif]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const prixHTCents = Math.round(parseFloat(prixHT || '0') * 100);
      const remise = remisePct ? parseFloat(remisePct) : undefined;
      if (isEdit && tarif) {
        await update.mutateAsync({
          id: tarif.id,
          payload: {
            contratRef: contratRef.trim(),
            prixHTCents, remisePct: remise,
            dateFin: dateFin || undefined,
            statut, notes: notes.trim() || undefined,
          },
        });
      } else {
        await create.mutateAsync({
          clientId, produitId, contratRef: contratRef.trim(),
          prixHTCents, remisePct: remise,
          dateDebut, dateFin: dateFin || undefined,
          statut, notes: notes.trim() || undefined,
        });
      }
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = clientId && produitId && contratRef.trim() && parseFloat(prixHT || '0') > 0 && dateDebut;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${tarif?.contratRef}` : 'Nouveau tarif contrat cadre'} subtitle="Prix négocié grand client (CIE / AGEROUTE / Orange / MTN…)" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Client *">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="ds-fi" style={{ width: '100%' }} required disabled={isEdit}>
              <option value="">— Sélectionner —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.companyName ?? c.fullName}</option>
              ))}
            </select>
          </Field>
          <Field label="Produit *">
            <select value={produitId} onChange={(e) => setProduitId(e.target.value)} className="ds-fi" style={{ width: '100%' }} required disabled={isEdit}>
              <option value="">— Sélectionner —</option>
              {produits.map((p) => (
                <option key={p.id} value={p.id}>{p.code} — {p.designation}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Référence contrat *">
            <input value={contratRef} onChange={(e) => setContratRef(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="CIE-2026-001" />
          </Field>
          <Field label="Prix HT négocié (FCFA) *">
            <input type="number" min="0" step="1" value={prixHT} onChange={(e) => setPrixHT(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="185000" />
          </Field>
          <Field label="Remise (%)">
            <input type="number" min="0" max="100" step="0.1" value={remisePct} onChange={(e) => setRemisePct(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="0" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Date début *">
            <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="ds-fi" required style={{ width: '100%' }} disabled={isEdit} />
          </Field>
          <Field label="Date fin">
            <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutTarif)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_TARIF_LABELS) as StatutTarif[]).map((s) => (
                <option key={s} value={s}>{STATUT_TARIF_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} placeholder="Conditions particulières, échéances de paiement…" />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
            {isEdit ? 'Enregistrer' : 'Créer le tarif'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
